import { Badge, MonetaryAmount } from '@components'
import { IEntry } from '@query'
import { getAccountPageUrl } from '@root/constants'
import { useBusinessContext } from '@root/context'
import { useVATCode } from '@root/query/hooks/useVATCodes'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import styled from 'styled-components'

interface Props {
  entry: IEntry
}

export const EntryRow: React.FC<Props> = ({ entry }) => {
  const [t] = useTranslation()
  const [isOpen, setOpen] = useState(false)
  const { slug } = useBusinessContext()
  const vatTypeOption = useVATCode(entry.vat_code)

  return (
    <>
      <StyledEntryRow
        onClick={() => setOpen(!isOpen)}
        data-test="entry-row"
        data-account-number={entry.account_number}
      >
        <EntryHeader>
          <AccountName>
            <span>{entry.account_name}</span>
          </AccountName>
          <HeaderDetails>
            <span data-test="entry-row-monetary-value">
              <MonetaryAmount value={entry.amount} />
            </span>
            {entry.is_debet ? (
              <Badge type="success">{t('document.entrySection.debet')}</Badge>
            ) : (
              <Badge type="warning">{t('document.entrySection.credit')}</Badge>
            )}
          </HeaderDetails>
        </EntryHeader>
      </StyledEntryRow>
      {isOpen && (
        <EntryDetails>
          <EntryDetailColumn>
            <EntryColumnName>{t('document.entrySection.accountNumber')}</EntryColumnName>
            <EntryDetailValue data-test="entry-row-details-account-number">
              <DetailAccountName to={getAccountPageUrl(slug, { id: entry.account })}>
                {entry.account_number} {entry.account_name}
              </DetailAccountName>
            </EntryDetailValue>

            <VatRow>
              <VatItem>
                <EntryColumnName>{t('document.entrySection.vatCode')}</EntryColumnName>
                <EntryDetailValue data-test="entry-row-details-account-vat-type">
                  {vatTypeOption?.label || '-'}
                </EntryDetailValue>
              </VatItem>
              <VatItem>
                <EntryColumnName>{t('document.entrySection.vatRate')}</EntryColumnName>
                <EntryDetailValue data-test="entry-row-details-vat-rate">
                  {entry.vat_rate ? `${entry.vat_rate} %` : '-'}
                </EntryDetailValue>
              </VatItem>
            </VatRow>
          </EntryDetailColumn>
        </EntryDetails>
      )}
    </>
  )
}

const StyledEntryRow = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  min-width: 0;
`

const EntryHeader = styled.div`
  flex: 1;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  min-width: 0;
`

const HeaderDetails = styled.div`
  flex: 0 0 auto;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
`

const AccountName = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

const EntryColumnName = styled.div`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.metalGray};
`
const EntryDetails = styled.div`
  background: ${({ theme }) => theme.colors.neutralGray};
  padding: ${({ theme }) => theme.spacing.sm}rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  display: flex;
  flex-wrap: wrap;
`

const EntryDetailColumn = styled.div`
  flex: 1;
`

const EntryDetailValue = styled.div`
  &:not(:last-of-type) {
    margin-bottom: ${({ theme }) => theme.spacing.sm}rem;
  }
`

const VatRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.lg}rem;
  margin-top: ${({ theme }) => theme.spacing.sm}rem;
  justify-content: space-between;
`

const VatItem = styled.div`
  min-width: 120px;
`

const DetailAccountName = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  font-variant-numeric: tabular-nums;
  flex: 1;
  color: ${({ theme }) => theme.colors.neutralBlack};
`
